import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Eye, FileText, History, ImageIcon, X } from 'lucide-react'
import {
  FILE_TYPES,
  allFields,
  formatFileSize,
  type DocumentInfo,
  type FormSchema,
} from '@concours/shared'
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DocumentPreviewModal,
  Textarea,
  useAuth,
  useToast,
  type BadgeVariant,
} from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'
import { when } from '../../lib/competition-types'
import { APPLICATIONS_KEY } from './ApplicationsPage'

const SCAN_VARIANT: Record<DocumentInfo['scanStatus'], BadgeVariant> = {
  pending: 'warning',
  clean: 'success',
  infected: 'danger',
  error: 'danger',
}
const REVIEW_VARIANT: Record<DocumentInfo['reviewStatus'], BadgeVariant> = {
  pending: 'neutral',
  accepted: 'success',
  rejected: 'danger',
}

/**
 * Pièces d'un dossier (cahier §10.2, TODO phase 10) : consultation dans la
 * visionneuse (journalisée), décision motivée, historique des remplacements.
 */
export function DocumentsCard({
  applicationId,
  schema,
  documents,
  canReview,
}: {
  applicationId: string
  schema: FormSchema
  documents: DocumentInfo[]
  canReview: boolean
}) {
  const [open, setOpen] = useState<DocumentInfo | null>(null)
  const fields = allFields(schema).filter((f) => FILE_TYPES.includes(f.type))
  if (!fields.length) return null

  return (
    <Card>
      <CardHeader title="Pièces justificatives" />
      <CardBody>
        <ul className="doc-review-list">
          {fields.map((field) => {
            const versions = documents.filter((d) => d.fieldKey === field.key)
            const current = versions.find((d) => d.current)
            const previous = versions.filter((d) => !d.current).reverse()
            const Icon = field.type === 'image' ? ImageIcon : FileText
            return (
              <li key={field.key}>
                <div className="doc-review-head">
                  <Icon aria-hidden="true" size={18} />
                  <strong>{field.label}</strong>
                  {field.required && <span className="text-muted">obligatoire</span>}
                </div>
                {current ? (
                  <DocumentRow doc={current} onOpen={() => setOpen(current)} />
                ) : (
                  <p className="text-muted doc-review-empty">Pièce non fournie.</p>
                )}
                {previous.length > 0 && (
                  <details className="doc-history">
                    <summary>
                      <History aria-hidden="true" size={14} /> {previous.length} version
                      {previous.length > 1 ? 's' : ''} précédente{previous.length > 1 ? 's' : ''}
                    </summary>
                    {previous.map((doc) => (
                      <DocumentRow key={doc.id} doc={doc} onOpen={() => setOpen(doc)} />
                    ))}
                  </details>
                )}
              </li>
            )
          })}
        </ul>
        <p
          className="text-muted"
          style={{ margin: 'var(--space-4) 0 0', fontSize: 'var(--text-sm)' }}
        >
          Chaque consultation d’une pièce est enregistrée au journal d’audit. Un fichier n’est
          accessible qu’après une analyse antivirus saine.
        </p>
      </CardBody>
      {open && (
        <ReviewModal
          applicationId={applicationId}
          doc={open}
          label={fields.find((f) => f.key === open.fieldKey)?.label ?? open.fieldKey}
          canReview={canReview && open.current}
          onClose={() => setOpen(null)}
        />
      )}
    </Card>
  )
}

function DocumentRow({ doc, onOpen }: { doc: DocumentInfo; onOpen: () => void }) {
  return (
    <div className="doc-review-row" data-current={doc.current || undefined}>
      <div className="grow" style={{ minWidth: 0 }}>
        <p className="doc-name">{doc.name}</p>
        <p className="doc-meta">
          {formatFileSize(doc.size)} · déposée le {when(doc.createdAt)}
          {doc.afterSubmission && ' · complément'}
          {doc.replacedAt && ` · remplacée le ${when(doc.replacedAt)}`}
          {doc.removedAt && ` · retirée le ${when(doc.removedAt)}`}
        </p>
        <p className="doc-meta">
          <Badge variant={SCAN_VARIANT[doc.scanStatus]} dot>
            {doc.scanLabel}
          </Badge>
          <Badge variant={REVIEW_VARIANT[doc.reviewStatus]} dot>
            {doc.reviewLabel}
          </Badge>
          {doc.reviewReason && <span>Motif : {doc.reviewReason}</span>}
        </p>
      </div>
      <Button
        variant={doc.current ? 'secondary' : 'ghost'}
        size="sm"
        icon={<Eye />}
        disabled={!doc.available}
        onClick={onOpen}
        aria-label={`Consulter ${doc.name}`}
      >
        Consulter
      </Button>
    </div>
  )
}

function ReviewModal({
  applicationId,
  doc,
  label,
  canReview,
  onClose,
}: {
  applicationId: string
  doc: DocumentInfo
  label: string
  canReview: boolean
  onClose: () => void
}) {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [reason, setReason] = useState(doc.reviewReason ?? '')
  const url = `/admin/applications/${applicationId}/documents/${doc.id}`
  // Chaque ouverture = une consultation journalisée (pas de cache).
  const file = useQuery({
    // Hors du préfixe du dossier : invalider le dossier ne retélécharge pas la pièce.
    queryKey: ['admin', 'document-file', doc.id],
    queryFn: () =>
      session.api.download(`${url}/file`, { query: { inline: 1 } }).then((r) => r.blob),
    retry: false,
    gcTime: 10_000,
    staleTime: Infinity,
  })

  const review = useMutation({
    mutationFn: (decision: 'accepted' | 'rejected') =>
      session.api.post(`${url}/review`, { decision, reason: reason.trim() || null }),
    onSuccess: async (_res, decision) => {
      toast.success(decision === 'accepted' ? 'Pièce acceptée' : 'Pièce refusée')
      await queryClient.invalidateQueries({ queryKey: [...APPLICATIONS_KEY, applicationId] })
      onClose()
    },
    onError: (error) => {
      if (!fieldErrors(error).reason) toast.error(errorMessage(error))
    },
  })

  return (
    <DocumentPreviewModal
      open
      onClose={onClose}
      title={label}
      description={`${doc.name} · ${formatFileSize(doc.size)} · SHA-256 ${doc.sha256.slice(0, 12)}…`}
      blob={file.data ?? null}
      error={file.isError ? errorMessage(file.error) : undefined}
      fileName={doc.name}
      actions={
        canReview && (
          <>
            <Button
              variant="danger"
              icon={<X />}
              loading={review.isPending && review.variables === 'rejected'}
              disabled={reason.trim().length < 10}
              onClick={() => review.mutate('rejected')}
            >
              Refuser
            </Button>
            <Button
              variant="primary"
              icon={<Check />}
              loading={review.isPending && review.variables === 'accepted'}
              onClick={() => review.mutate('accepted')}
            >
              Accepter
            </Button>
          </>
        )
      }
    >
      {canReview && (
        <Textarea
          label="Motif (obligatoire pour un refus)"
          rows={2}
          hint="10 caractères minimum. Le motif est communiqué au candidat."
          value={reason}
          error={fieldErrors(review.error).reason}
          onChange={(e) => setReason(e.target.value)}
        />
      )}
    </DocumentPreviewModal>
  )
}
